import { QuickFiltersPanel, QuickFiltersHeader, QuickFiltersRow, AppliedFiltersRow } from "../../layouts/QuickFilters.jsx";
import React, {useState, useEffect, useRef, useMemo} from "react";
import { FaTimes as FaClose, FaFilter, FaTimes, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import server from '../../../api/server.js';
import '../../../dashboard-components/loader.css';
import { useNavigate, useLocation } from 'react-router-dom';
import CustomerDetails from "../customers/CustomerDetails.jsx"
import ModernTimeDropdown from "../../layouts/modernDropdown.jsx";
import ModernDateRangePicker from "../../layouts/ModernDateRangePicker.jsx";
import { useToast } from '../../../context/ToastContext.jsx';
import { Download, ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import CustomerAdvancedFiltersPanel from "../../layouts/CustomerAdvancedFiltersPanel";
import {
  initializeFiltersFromUrl,
  updateUrlWithFilters,
  getListPageFromLocation,
  extractModuleParameterFiltersFromFlatFilters,
  normalizeInteractionFiltersFromUrl,
  clearAdvancedFilterField,
  getCrmLeadCreatedAtApiArgs,
  setCrmLeadCreatedAtPreset,
  applyCrmLeadCreatedAtFromSegmentFilter,
} from '../../../utils/filterUtils.js';
import CrmLeadCreatedAtFilterChips, { shouldHideCrmLeadCreatedAtChipKey } from "../../layouts/CrmLeadCreatedAtFilterChips";
import CustomerDownloadParametersModal from "./CustomerDownloadParametersModal.jsx";
import {
  buildAdditionalTagFilterConditions,
  buildDashboardFilterPayload,
  getAdditionalTagFilterValues,
} from "../../../utils/filterPayloadBuilder.js";

const toSnakeCase = (value) => String(value || '').trim().replace(/([A-Z])/g, '_$1').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '').toLowerCase();
const asValidBetween = (value) => {
  if (!value || typeof value !== 'object') return null;
  const lowerRaw = value.lower;
  const upperRaw = value.upper;
  const lower = lowerRaw === '' || lowerRaw === null || lowerRaw === undefined ? null : Number(lowerRaw);
  const upper = upperRaw === '' || upperRaw === null || upperRaw === undefined ? null : Number(upperRaw);
  const hasLower = lower !== null && !Number.isNaN(lower);
  const hasUpper = upper !== null && !Number.isNaN(upper);
  if (!hasLower && !hasUpper) return null;
  return { lower: hasLower ? lower : null, upper: hasUpper ? upper : null };
};
export const buildModuleParameterFilters = (filters = {}, advancedFilters = {}) => {
  const output = [
    ...extractModuleParameterFiltersFromFlatFilters(filters),
    ...buildAdditionalTagFilterConditions(advancedFilters),
  ];
  const bucketFilter = advancedFilters.time_series_bucket_filter;
  const bucketBetween = asValidBetween(bucketFilter?.value);
  if (
    bucketFilter?.operator === 'between' &&
    ['first_interaction_time', 'last_interaction_time', 'crm_lead_created_at'].includes(bucketFilter?.field) &&
    bucketBetween &&
    bucketBetween?.lower !== null &&
    bucketBetween?.upper !== null &&
    Number.isInteger(bucketBetween.lower) &&
    Number.isInteger(bucketBetween.upper) &&
    bucketBetween.upper >= bucketBetween.lower
  ) {
    output.push({
      field: bucketFilter.field,
      operator: 'between',
      value: bucketBetween,
    });
  }
  if (Array.isArray(advancedFilters.business_insights_multiple)) {
    advancedFilters.business_insights_multiple.forEach((item) => {
      const field = toSnakeCase(item?.business_insights_parameter);
      const value = item?.business_insights_parameter_value;
      const betweenValue = asValidBetween(value);
      if (field && betweenValue) {
        output.push({ field, operator: 'between', value: betweenValue, module: 'business_insight' });
      } else if (field && value !== null && value !== undefined && value !== '') {
        output.push({ field, operator: Array.isArray(value) ? 'in' : 'eq', value, module: 'business_insight' });
      }
    });
  }
  if (advancedFilters.custom_module_parameter_name && advancedFilters.custom_module_parameter_answer !== null && advancedFilters.custom_module_parameter_answer !== undefined && advancedFilters.custom_module_parameter_answer !== '') {
    const betweenValue = asValidBetween(advancedFilters.custom_module_parameter_answer);
    output.push({
      field: toSnakeCase(advancedFilters.custom_module_parameter_name),
      operator: betweenValue ? 'between' : Array.isArray(advancedFilters.custom_module_parameter_answer) ? 'in' : 'eq',
      value: betweenValue || advancedFilters.custom_module_parameter_answer,
      module: 'custom_module'
    });
  }
  if (Array.isArray(advancedFilters.custom_module_multiple)) {
    advancedFilters.custom_module_multiple.forEach((item) => {
      const field = toSnakeCase(item?.custom_module_parameter_name);
      const value = item?.custom_module_parameter_answer;
      const betweenValue = asValidBetween(value);
      if (field && betweenValue) {
        output.push({ field, operator: 'between', value: betweenValue, module: 'custom_module' });
      } else if (field && value !== null && value !== undefined && value !== '') {
        output.push({ field, operator: Array.isArray(value) ? 'in' : 'eq', value, module: 'custom_module' });
      }
    });
  }
  const dedupe = [];
  const seen = new Set();
  output.forEach((item) => {
    const key = JSON.stringify([item.field, item.operator, item.module, item.value]);
    if (seen.has(key)) return;
    seen.add(key);
    dedupe.push(item);
  });
  return dedupe;
};
const buildParameterQueryFilters = (conditions = []) => {
  const query = {};
  conditions.forEach((condition) => {
    if (!condition.module) return;
    const key = condition.module ? `${condition.module}_${condition.field}` : condition.field;
    if (condition.operator === 'between' && condition.value && typeof condition.value === 'object') {
      const lowerKey = condition.module === 'agent_feedback' ? `${key}_score_lower` : `${key}_lower`;
      const upperKey = condition.module === 'agent_feedback' ? `${key}_score_upper` : `${key}_upper`;
      if (condition.value.lower !== undefined && condition.value.lower !== null && condition.value.lower !== '') {
        query[lowerKey] = condition.value.lower;
      }
      if (condition.value.upper !== undefined && condition.value.upper !== null && condition.value.upper !== '') {
        query[upperKey] = condition.value.upper;
      }
      return;
    }
    if (condition.value !== undefined && condition.value !== null && condition.value !== '') {
      query[key] = condition.value;
    }
  });
  return query;
};

async function readDownloadPreviewTotal(res) {
  if (!res?.ok) return null;
  try {
    const data = await res.json();
    const n = (v) => (typeof v === "number" && !Number.isNaN(v) ? v : null);
    return (
      n(data.total) ??
      n(data.total_count) ??
      n(data.total_records) ??
      n(data.count) ??
      n(data.record_count) ??
      null
    );
  } catch {
    return null;
  }
}

const normalizeCustomerRecord = (record = {}) => ({
  ...record,
  id: record.id ?? record.customerId ?? record.customer_id ?? '',
  latestemployeeid:
    record.latestemployeeid ?? record.latestEmployeeId ?? record.employee_id ?? '',
  firstinteraction:
    record.firstinteraction ??
    record.firstInteraction ??
    record.first_interaction_time ??
    null,
  lastinteraction:
    record.lastinteraction ??
    record.lastInteraction ??
    record.last_interaction_time ??
    null,
});

const formatCustomerRecordDate = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numericValue = Number(value);
  const date = Number.isFinite(numericValue)
    ? new Date(Math.abs(numericValue) < 1e12 ? numericValue * 1000 : numericValue)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const options = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  return date.toLocaleString('en-GB', options).replace(',', '');
};

const CustomerRecords = ({serviceID, clientID, segment, type, hide_filters = false, onStaleDrilldown}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const shouldHideFilters = Boolean(hide_filters || segment?.hide_filters);
  const exactCountDrilldown = segment?.custom_report_drilldown || null;
  const appliedFilterSummary = Array.isArray(segment?.applied_filter_summary)
    ? segment.applied_filter_summary
    : [];
  const isReportDrilldown = Boolean(segment);
  const isEmbeddedModal = type === 'modal-open';

  const [filterMode, setFilterMode] = useState("quick");
  const [customerIds, setCustomerIds] = useState([]);
  const [conversationIds, setConversationIds] = useState([]);

  // Default filter values
  const defaultFilters = {
    employee: '',
    team_name: '',
    firstinteraction: '',
    firstinteraction_range_type: 'between',
    lastinteraction: isReportDrilldown ? '' : 'Last 7 Days',
    lastinteraction_range_type: 'between',
    customer_id: '',
    'First Interaction Start': '',
    'First Interaction End': '',
    'Last Interaction Start': '',
    'Last Interaction End': ''
  };

  const defaultAdvancedFilters = {
    refusalCategory: '',
    customerPotential: "",
    customer_outcome: '',
    openConcerns: '',
    id: '',
    interactions: '',
    concernCategory: '',
    concernSubcategory: '',
    queryCategory: '',
    querySubcategory: '',
    business_insights_multiple: [],
    disposition: '',
    subdisposition: '',
    lead_source: '',
    primary_language: '',
    secondary_languages: '',
    lead_sub_source: '',
    tag: '',
    tag2: '',
    tag3: '',
    tag4: '',
    tag5: '',
    tag6: '',
    tag7: '',
    tag8: '',
    tag9: '',
    tag10: '',
    tag11: '',
    tag12: '',
    tag13: '',
    tag14: '',
    tag15: '',
    tag16: '',
    tag17: '',
    tag18: '',
    tag19: '',
    tag20: '',
    tag21: '',
    tag22: '',
    tag23: '',
    tag24: '',
    tag25: '',
    tag26: '',
    tag27: '',
    tag28: '',
    tag29: '',
    tag30: '',
    crm_lead_stage: '',
    campaign_id: '',
    crm_lead_sub_stage: '',
    customer_phone_number: '',
    crm_location: '',
    crm_lead_created_at: '',
    crm_lead_created_at_start: '',
    crm_lead_created_at_end: '',
    crm_lead_created_at_range_type: 'between',
    time_series_bucket_filter: null,
    custom_module_parameter_name: '',
    custom_module_parameter_answer: '',
    custom_module_multiple: []
  };

  // Initialize filters from URL or use defaults (only when not passed via segment prop)
  const initialFilterState = !segment ? initializeFiltersFromUrl(location, defaultFilters, defaultAdvancedFilters) : {
    filters: defaultFilters,
    advancedFilters: defaultAdvancedFilters,
    customStart: '',
    customEnd: ''
  };

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [filteredConversations, setFilteredConversations] = useState([]);
    const [email, setEmail] = useState(null)
    const [client_id, setClient_id] = useState(null)
    const [service_id, setService_id] = useState(null)
    const [totalRecords, setTotalRecords] = useState(0)
    const [originalSize, setOriginalSize] = useState(0)
    const [openType, setOpenType] = useState(null)
    const [showCustomerPage, setShowCustomerPage] = useState(false)
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [selectedCustomerID, setSelectedCustomerID] = useState(null)
    const [segmentParameterData, setSegmentParameterData] = useState({'type':null, 'id': null, 'name': null})
    const [role] = useState(()=> {
      const userCookie = getCookie('dashboard_login_user_creds');
      if (userCookie) {
        const userData = JSON.parse(userCookie);
        const role = userData.role
        return role
      }
      return 'admin';
    })
    const [expandedSections, setExpandedSections] = useState({
      businessInsights: false,
      concernsAndQueries: false,
      dispositionAndSubdisposition: false,
      customModule: false
    });
    const [filters, setFilters] = useState(() =>
      normalizeInteractionFiltersFromUrl(
        { ...defaultFilters, ...initialFilterState.filters },
        { customStart: initialFilterState.customStart, customEnd: initialFilterState.customEnd }
      )
    );
    const [advancedFilters, setAdvancedFilters] = useState(initialFilterState.advancedFilters);
      const [page, setPage] = useState(() => (!segment && !type ? getListPageFromLocation(location, 'custPage') : 1));
      const [totalPages, setTotalPages] = useState(1);
      const [apiCallRunning, setApiCallRunning] = useState(false)
      const [loading, setLoading] = useState(false)
      // Ref for aborting customer page requests
      const customerAbortRef = useRef(null);
      const [dummyFilterData, setDummyFilterData] = useState({
        employees: [],
        modes: ['Call', 'Email', 'Chat'],
        firstinteraction: ['Today',
          'Yesterday',
          'This Week',
          "This Month",
          "Last Month",
          "Last 7 Days",
          "Last 30 Days",
          "Last 3 Months",
          "Custom Range"],
        lastinteraction: ['Today',
          'Yesterday',
          'This Week',
          "This Month",
          "Last Month",
          "Last 7 Days",
          "Last 30 Days",
          "Last 3 Months",
          "Custom Range"]
      });
      const [multiSelectFields, setMultiSelectFields] = useState([]);
      const isMultiSelectField = (key) => Array.isArray(multiSelectFields) && multiSelectFields.includes(key);
      let [dummyAdvancedFilterData, setDummyAdvancedFilterData] =useState({
        refusal_category: [],
        open_concerns_and_queries: ['Yes', 'No'],
        customer_outcome: [],
        customer_potential: [],
        business_insight_parameters: [],
        concern_categories: [],
        concern_subcategories: [],
        query_categories: [],
        query_subcategories: [],
        disposition: [],
        subdisposition: [],
        lead_sources: [],
        primary_languages: [],
        secondary_languages: [],
        lead_sub_sources: [],
        campaign_ids: [],
        tags: [],
        tags2: [],
        tags3: [],
        tags4: [],
        tags5: [],
        tags6: [],
        tags7: [],
        tags8: [],
        tags9: [],
        tags10: [],
        tags11: [],
        tags12: [],
        tags13: [],
        tags14: [],
        tags15: [],
        tags16: [],
        tags17: [],
        tags18: [],
        tags19: [],
        tags20: [],
        tags21: [],
        tags22: [],
        tags23: [],
        tags24: [],
        tags25: [],
        tags26: [],
        tags27: [],
        tags28: [],
        tags29: [],
        tags30: [],
        lead_stages: [],
    lead_sub_stages: [],
    custom_module: []
      })
      const [conversations, setConversations] = useState([]);
      // Preserve scroll position of the customers list across opening/closing details
      const tableScrollRef = useRef(null);
      const savedScrollTopRef = useRef(0);
      const [sortConfig, setSortConfig] = useState({ field: null, direction: null })
      
      // Update URL whenever filters change (only if not using segment prop)
      useEffect(() => {
        if (!segment && !type && client_id && service_id) { // Only update URL for standalone usage, not when embedded as modal
          // Debounce URL updates to avoid conflicts with navigation
          const timeoutId = setTimeout(() => {
            const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
            const queryParameterFilters = buildParameterQueryFilters(moduleParameterFilters);
            const advancedFiltersForUrl = { ...advancedFilters };
            delete advancedFiltersForUrl.business_insights_multiple;
            delete advancedFiltersForUrl.custom_module_multiple;
            delete advancedFiltersForUrl.custom_module_parameter_name;
            delete advancedFiltersForUrl.custom_module_parameter_answer;
            updateUrlWithFilters(navigate, location, { ...filters, ...queryParameterFilters }, advancedFiltersForUrl, '', '', page, { pageParam: 'custPage' });
          }, 300);
          
          return () => clearTimeout(timeoutId);
        }
      }, [filters, advancedFilters, segment, type, client_id, service_id, page]); // Removed navigate and location from deps

      useEffect(() => {
        if (segment || type) return;
        const fromUrl = getListPageFromLocation(location, 'custPage');
        setPage((p) => (p !== fromUrl ? fromUrl : p));
      }, [location.search, segment, type]);

      function getCookie(name) {
        function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
        var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
        return match ? match[1] : null;
    }
    const buildExactAdditionalFilters = () => {
      const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
      const [
        crmLeadCreatedAt,
        crmLeadCreatedAtStart,
        crmLeadCreatedAtEnd,
        crmLeadCreatedAtRangeType,
      ] = getCrmLeadCreatedAtApiArgs(advancedFilters);
      const payload = buildDashboardFilterPayload({
        employee_id: filters.employee,
        team_name: filters.team_name,
        first_interaction_time: filters.firstinteraction,
        first_interaction_custom_start: filters['First Interaction Start'],
        first_interaction_custom_end: filters['First Interaction End'],
        first_interaction_range_type: filters.firstinteraction_range_type,
        customer_id: filters.customer_id,
        last_interaction_time: filters.lastinteraction,
        last_interaction_custom_start: filters['Last Interaction Start'],
        last_interaction_custom_end: filters['Last Interaction End'],
        last_interaction_range_type: filters.lastinteraction_range_type,
        interactions: advancedFilters.interactions,
        open_concerns: advancedFilters.openConcerns,
        customer_potential: advancedFilters.customerPotential,
        concern_category: advancedFilters.concernCategory,
        concern_subcategory: advancedFilters.concernSubcategory,
        query_category: advancedFilters.queryCategory,
        query_subcategory: advancedFilters.querySubcategory,
        disposition: advancedFilters.disposition,
        subdisposition: advancedFilters.subdisposition,
        lead_source: advancedFilters.lead_source,
        primary_language: advancedFilters.primary_language,
        secondary_languages: advancedFilters.secondary_languages,
        lead_sub_source: advancedFilters.lead_sub_source,
        tag: advancedFilters.tag,
        tag2: advancedFilters.tag2,
        tag3: advancedFilters.tag3,
        tag4: advancedFilters.tag4,
        tag5: advancedFilters.tag5,
        tag6: advancedFilters.tag6,
        tag7: advancedFilters.tag7,
        tag8: advancedFilters.tag8,
        tag9: advancedFilters.tag9,
        tag10: advancedFilters.tag10,
        tag11: advancedFilters.tag11,
        tag12: advancedFilters.tag12,
        tag13: advancedFilters.tag13,
        tag14: advancedFilters.tag14,
        tag15: advancedFilters.tag15,
        tag16: advancedFilters.tag16,
        tag17: advancedFilters.tag17,
        tag18: advancedFilters.tag18,
        tag19: advancedFilters.tag19,
        tag20: advancedFilters.tag20,
        tag21: advancedFilters.tag21,
        tag22: advancedFilters.tag22,
        tag23: advancedFilters.tag23,
        tag24: advancedFilters.tag24,
        tag25: advancedFilters.tag25,
        tag26: advancedFilters.tag26,
        tag27: advancedFilters.tag27,
        tag28: advancedFilters.tag28,
        tag29: advancedFilters.tag29,
        tag30: advancedFilters.tag30,
        crm_lead_stage: advancedFilters.crm_lead_stage,
        crm_lead_sub_stage: advancedFilters.crm_lead_sub_stage,
        customer_phone_number: advancedFilters.customer_phone_number,
        crm_location: advancedFilters.crm_location,
        campaign_id: advancedFilters.campaign_id,
        crm_lead_created_at: crmLeadCreatedAt,
        crm_lead_created_at_start: crmLeadCreatedAtStart,
        crm_lead_created_at_end: crmLeadCreatedAtEnd,
        crm_lead_created_at_range_type: crmLeadCreatedAtRangeType,
        module_parameter_filters: moduleParameterFilters,
      });
      return payload.filter || { and: [] };
    };

    const downloadConversations = async (parametersJson, filterMode, parsedIds) => {
      try {
        const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
        const res = exactCountDrilldown && filterMode === 'quick'
          ? await server.getCustomReportDrilldown({
              client_id,
              service_id,
              email,
              menu: 'custom-reports',
              ...exactCountDrilldown,
              additional_filters: buildExactAdditionalFilters(),
              perform_download: true,
              parameters: parametersJson,
            })
          : await server.getDownloadCustomerData(
          client_id,
          service_id,
          email,
          'customers',
          undefined,
          1,
          filters.employee,
          filters.firstinteraction,
          filters.customer_id,
          filters.lastinteraction,
          advancedFilters.interactions,
          advancedFilters.openConcerns,
          filters['First Interaction Start'],
          filters['First Interaction End'],
          filters['Last Interaction Start'],
          filters['Last Interaction End'],
          segmentParameterData['type'],
          segmentParameterData['id'],
          segmentParameterData['name'],
          totalRecords,
          advancedFilters.customerPotential,
          advancedFilters.business_insights_multiple,
          advancedFilters.concernCategory,
          advancedFilters.concernSubcategory,
          advancedFilters.queryCategory,
          advancedFilters.querySubcategory,
          advancedFilters.disposition,
          advancedFilters.subdisposition,
          advancedFilters.lead_source,
          advancedFilters.primary_language,
          advancedFilters.secondary_languages,
          advancedFilters.lead_sub_source,
          advancedFilters.tag,
          advancedFilters.tag2,
          advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters),
          advancedFilters.crm_lead_stage,
          advancedFilters.crm_lead_sub_stage,
          advancedFilters.customer_phone_number,
          advancedFilters.crm_location,
          advancedFilters.campaign_id,
          'downloads',
          advancedFilters.custom_module_parameter_name,
          advancedFilters.custom_module_parameter_answer,
          undefined,
          parametersJson,
          true,
          ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name,
          filterMode,
          parsedIds,
          moduleParameterFilters
        );
        if (!res.ok) {
          let message = 'Could not queue your download. Please try again later';
          try {
            const payload = await res.json();
            if (payload?.message || payload?.error) {
              message = payload.message || payload.error;
            }
            if (
              exactCountDrilldown &&
              ["stale_drilldown_context", "expired_drilldown_context"].includes(payload?.error)
            ) {
              onStaleDrilldown?.(payload.error);
            }
          } catch (error) {
            // Keep the safe fallback when the response body is not JSON.
          }
          showToast(message, 'error');
          return;
        }
        showToast("We're preparing your download. You'll receive an email with the link shortly.", "success");
      } catch (error) {
        showToast('Something went wrong. Please try again later', 'error');
      }
    };

    const captureRecords = async (payload) => {
      setIsDownloadModalOpen(false);
      let parametersJson = payload['parametersJson'], filterMode = payload['filterMode'], parsedIds = payload['parsedIds'];
      await downloadConversations(parametersJson, filterMode, parsedIds);
    };

    const handleQuickFilterChange = (e, key) => {
      const rawValue = e.target.value;
      const value = isMultiSelectField(key)
        ? (Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []))
        : rawValue;
      
      // If changing time filter and it's not "Custom Range", clear the custom date values
      if (key === 'firstinteraction' && !Array.isArray(value) && value !== 'Custom Range') {
        setFilters((prev) => ({
          ...prev,
          firstinteraction: value,
          firstinteraction_range_type: 'between',
          'First Interaction Start': '',
          'First Interaction End': '',
        }));
      } else if (key === 'lastinteraction' && !Array.isArray(value) && value !== 'Custom Range') {
        setFilters((prev) => ({
          ...prev,
          lastinteraction: value,
          lastinteraction_range_type: 'between',
          'Last Interaction Start': '',
          'Last Interaction End': '',
        }));
      } else {
        setFilters((prev) => ({ ...prev, [key]: value }));
      }
    };
      const handleAdvancedInputChange = (e, key) => {
        const value = e.target.value;
        if (key === 'crm_lead_created_at') {
          setAdvancedFilters((prev) => setCrmLeadCreatedAtPreset(prev, value));
          return;
        }
        setAdvancedFilters((prev) => ({
          ...prev,
          [key]: value,
        }));
      };
    
      // useEffect(() => {
      //   const prevSize = conversations.length
      //     const newFiltered = conversations.filter((convo) => {
      //       return (
      //         // Quick filters
      //         (filters.employee === '' || convo.latestemployeeid.includes(filters.employee)) &&
      //         (filters.firstinteraction === '' || checkTimeFilter(convo.firstinteraction, filters.firstinteraction)) &&
      //         (filters.lastinteraction === '' || checkTimeFilter(convo.lastinteraction, filters.lastinteraction)) &&
      //         (filters.id === '' || convo.id.includes(filters.id.toUpperCase())) &&      
      //         // Advanced filters
      //         (advancedFilters.refusalCategory === '' || convo.refusalReasons === advancedFilters.refusalCategory) &&
      //         (advancedFilters.customer_outcome === '' || convo.outcome === advancedFilters.customer_outcome) &&
      //         (advancedFilters.openConcerns === '' || convo.openConcernsAndQueries === advancedFilters.openConcerns) &&
      //         (advancedFilters.id === '' || convo.id?.includes(advancedFilters.id)) &&
      //         (advancedFilters.interactions === '' || checkInteractionsCount(convo.interactions, advancedFilters.interactions))
      //       );
      //     });
      //     const newSize = newFiltered.length
      //     if(newSize !== prevSize){
      //       setTotalRecords(newSize)
      //     }else{
      //       setTotalRecords(originalSize)
      //     }
      //     setFilteredConversations(newFiltered);
      //     setPage(1); // reset to first page whenever filters change
      //   }, [filters, advancedFilters]);
    const toggleAdvancedFilterPanel = () => {
        setShowAdvancedFilters(!showAdvancedFilters);
    };
    
    const get_customer_page_details = async (client_id, service_id, page_size) => {
      // abort previous
      if(customerAbortRef.current){ customerAbortRef.current.abort(); }
      const controller = new AbortController();
      customerAbortRef.current = controller;

      try {
        setApiCallRunning(true);
        setLoading(true);
        const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);

        const res = exactCountDrilldown
          ? await server.getCustomReportDrilldown(
              {
                client_id,
                service_id,
                email,
                menu: 'custom-reports',
                ...exactCountDrilldown,
                additional_filters: buildExactAdditionalFilters(),
                pagination: { page_number: page_size },
              },
              controller.signal
            )
          : await server.getCustomerPageDetails(client_id, service_id, email, 'customers', undefined, page_size, filters.employee, filters.firstinteraction, filters.customer_id, filters.lastinteraction, advancedFilters.interactions, advancedFilters.openConcerns, filters['First Interaction Start'], filters['First Interaction End'], filters['Last Interaction Start'], filters['Last Interaction End'], segmentParameterData['type'], segmentParameterData['id'], segmentParameterData['name'], advancedFilters.customerPotential, advancedFilters.business_insights_multiple, advancedFilters.concernCategory, advancedFilters.concernSubcategory, advancedFilters.queryCategory, advancedFilters.querySubcategory, advancedFilters.disposition, advancedFilters.subdisposition, advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters), advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location, advancedFilters.campaign_id, advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer, controller.signal, moduleParameterFilters, ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name);
        if(res.status === 200){
          const resp = await res.json();
          const records = Array.isArray(resp.data)
            ? resp.data.map(normalizeCustomerRecord)
            : [];
          setConversations(records);
          setFilteredConversations(records);
          const totalRecords = resp.total_records;
          setTotalRecords(totalRecords);
          setOriginalSize(totalRecords);
          setTotalPages(Math.ceil(totalRecords / 50));
          setLoading(false);
        }else{
          let message = 'Something went wrong. Please try again later';
          try {
            const payload = await res.json();
            if (payload?.message) message = payload.message;
            if (
              exactCountDrilldown &&
              ["stale_drilldown_context", "expired_drilldown_context"].includes(payload?.error)
            ) {
              showToast("The report changed. Refreshing the latest result...", "info");
              onStaleDrilldown?.(payload.error);
              setApiCallRunning(false);
              setLoading(false);
              return;
            }
          } catch (error) {
            // Keep the safe fallback message when the response is not JSON.
          }
          showToast(message, 'error')
          setApiCallRunning(false)
          setLoading(false)
        }
        setApiCallRunning(false);
      }catch(error){
        if(error.name === 'AbortError'){
          setApiCallRunning(false);
          return;
        }
        setApiCallRunning(false);
        setLoading(false);
      }
    }

  // fetch filter details once
  const fetchFilterDetailsOnceCustomers = async () => {
    try{
      const controller = new AbortController();
      const res = await server.getCustomerFilters(client_id, service_id, email, 'home', undefined, controller.signal);
      if(res.status === 200){
        const data = await res.json();
        setMultiSelectFields(Array.isArray(data.multiselects_list) ? data.multiselects_list : []);
        setDummyAdvancedFilterData(prev=>({
          ...prev,
          employees: data.employee_id,
          teams: data.team_names || data.team_name || data.teams || [],
          customer_outcome: data.customer_outcome,
          refusal_category: data.refusal_category,
          customer_potential: data.customer_potential,
          business_insight_parameters: data.business_insights,
          concern_categories: data.concerns_category,
          concern_subcategories: data.concerns_subcategory,
          query_categories: data.query_category,
          query_subcategories: data.query_subcategory,
          disposition: data.disposition,
          subdisposition: data.subdisposition,
          lead_sources: data.lead_sources,
          primary_languages: data.primary_languages || data.primary_language || [],
          secondary_languages: data.secondary_languages || [],
          lead_sub_sources: data.lead_sub_sources,
          tags: data.tags,
          tags2: data.tags2 || data.tag2 || data.tags,
          tags3: data.tags3 || data.tag3 || data.tags,
          tags4: data.tags4 || data.tag4 || data.tags,
          tags5: data.tags5 || data.tag5 || data.tags,
          tags6: data.tags6 || data.tag6 || data.tags,
          tags7: data.tags7 || data.tag7 || data.tags,
          tags8: data.tags8 || data.tag8 || data.tags,
          tags9: data.tags9 || data.tag9 || data.tags,
          tags10: data.tags10 || data.tag10 || data.tags,
          tags11: data.tags11 || data.tag11 || data.tags,
          tags12: data.tags12 || data.tag12 || data.tags,
          tags13: data.tags13 || data.tag13 || data.tags,
          tags14: data.tags14 || data.tag14 || data.tags,
          tags15: data.tags15 || data.tag15 || data.tags,
          tags16: data.tags16 || data.tag16 || data.tags,
          tags17: data.tags17 || data.tag17 || data.tags,
          tags18: data.tags18 || data.tag18 || data.tags,
          tags19: data.tags19 || data.tag19 || data.tags,
          tags20: data.tags20 || data.tag20 || data.tags,
          tags21: data.tags21 || data.tag21 || data.tags,
          tags22: data.tags22 || data.tag22 || data.tags,
          tags23: data.tags23 || data.tag23 || data.tags,
          tags24: data.tags24 || data.tag24 || data.tags,
          tags25: data.tags25 || data.tag25 || data.tags,
          tags26: data.tags26 || data.tag26 || data.tags,
          tags27: data.tags27 || data.tag27 || data.tags,
          tags28: data.tags28 || data.tag28 || data.tags,
          tags29: data.tags29 || data.tag29 || data.tags,
          tags30: data.tags30 || data.tag30 || data.tags,
          lead_stages: data.lead_stages,
          lead_sub_stages: data.lead_sub_stages,
          campaign_ids: data.campaign_ids,
          custom_module: data.custom_module
        }));
      }
    }catch(err){}
  }

  const parseDurationToSeconds = (value) => {
    if (value == null) return null;
    if (typeof value === 'number') return value;
    const str = String(value);
    if (str.includes(':')) {
      const parts = str.split(':').map((p) => Number(p));
      if (parts.some((n) => Number.isNaN(n))) return null;
      if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
      if (parts.length === 2) return parts[0] * 60 + parts[1];
      if (parts.length === 1) return parts[0];
    }
    const num = Number(str);
    return Number.isNaN(num) ? null : num;
  };

  const getValueForSort = (item, field) => {
    if (!item) return null;
    switch (field) {
      case 'firstinteraction':
      case 'lastinteraction':
        return item[field] != null ? Number(item[field]) : null;
      case 'id':
        return item.id != null ? String(item.id).toLowerCase() : null;
      case 'latestemployeeid':
        return item.latestemployeeid != null ? String(item.latestemployeeid).toLowerCase() : null;
      
      default: {
        const v = item[field];
        if (v == null) return null;
        return typeof v === 'number' ? v : String(v).toLowerCase();
      }
    }
  };

  const sortArray = (list, field, direction) => {
    if (!field || !direction) return list;
    const arr = [...list];
    const factor = direction === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = getValueForSort(a, field);
      const vb = getValueForSort(b, field);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulls last
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return factor * (va - vb);
      }
      return factor * String(va).localeCompare(String(vb));
    });
    return arr;
  };

  const arrowClass = (cfg, field, dir) => (cfg.field === field && cfg.direction === dir ? 'text-indigo-600' : 'text-gray-300 group-hover:text-gray-400');
  const getAriaSort = (cfg, field) => (cfg.field === field ? (cfg.direction === 'asc' ? 'ascending' : 'descending') : 'none');
  const cycleSort = (field, table) => {
    const cfg = sortConfig;
    let nextDir = 'asc';
    if (cfg.field === field) {
      nextDir = cfg.direction === 'asc' ? 'desc' : (cfg.direction === 'desc' ? null : 'asc');
    }
    setSortConfig(nextDir ? { field, direction: nextDir } : { field: null, direction: null });
  };

  const renderArrows = (cfg, field, setCfg) => {
    if (cfg.field === field && cfg.direction === 'asc') {
      return (
        <ArrowUp className={`h-3 w-3 ${arrowClass(cfg, field, 'asc')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'asc' });}} />
      );
    }
    if (cfg.field === field && cfg.direction === 'desc') {
      return (
        <ArrowDown className={`h-3 w-3 -mt-0.5 ${arrowClass(cfg, field, 'desc')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'desc' });}} />
      );
    }
    return (
      <>
        <ChevronsUpDown className={`h-3 w-3 ${arrowClass(cfg, field, 'none')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'asc' });}} />
      </>
    );
  };

  const renderMainArrows = (field) => renderArrows(sortConfig, field, setSortConfig);
  const moduleParameterQueryFilters = buildParameterQueryFilters(buildModuleParameterFilters(filters, advancedFilters));

  const displayConversations = useMemo(() => (
    sortConfig.field ? sortArray(filteredConversations, sortConfig.field, sortConfig.direction) : filteredConversations
  ), [filteredConversations, sortConfig]);

  // Function to get disposition keys (for the disposition dropdown)
  const getDispositionKeys = () => {
    if (!dummyAdvancedFilterData.subdisposition || typeof dummyAdvancedFilterData.subdisposition !== 'object') {
      return dummyAdvancedFilterData.disposition || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.subdisposition);
    return keys;
  };

  const getSubdispositionsForDisposition = (selectedDisposition) => {
    if (!dummyAdvancedFilterData.subdisposition) {
      return [];
    }
    
    // If no disposition selected, return all subdispositions
    if (!selectedDisposition) {
      if (typeof dummyAdvancedFilterData.subdisposition === 'object' && !Array.isArray(dummyAdvancedFilterData.subdisposition)) {
        // Flatten all subdispositions from all dispositions
        return Object.values(dummyAdvancedFilterData.subdisposition).flat();
      }
      return dummyAdvancedFilterData.subdisposition || [];
    }
    
    if (typeof dummyAdvancedFilterData.subdisposition === 'object' && !Array.isArray(dummyAdvancedFilterData.subdisposition)) {
      const subdispositions = dummyAdvancedFilterData.subdisposition[selectedDisposition] || [];
      return subdispositions;
    }
    
    return dummyAdvancedFilterData.subdisposition || [];
  };

  // Enhanced handler for disposition changes
  const handleDispositionChange = (e) => {
    const newDisposition = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      disposition: newDisposition
    }));
    
    if (newDisposition) {
      const validSubdispositions = getSubdispositionsForDisposition(newDisposition);
      const currentSubdisposition = advancedFilters.subdisposition;
      
      // If current subdisposition is not valid for new disposition, clear it
      if (currentSubdisposition && !validSubdispositions.includes(currentSubdisposition)) {
        setAdvancedFilters(prev => ({
          ...prev,
          subdisposition: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        subdisposition: ''
      }));
    }
  };
  // Function to get concern category keys (for the concern category dropdown)
  const getConcernCategoryKeys = () => {
    if (!dummyAdvancedFilterData.concern_subcategories || typeof dummyAdvancedFilterData.concern_subcategories !== 'object') {
      return dummyAdvancedFilterData.concern_categories || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.concern_subcategories);
    return keys;
  };

  const getConcernSubcategoriesForCategory = (selectedConcernCategory) => {
    if (!dummyAdvancedFilterData.concern_subcategories) {
      return [];
    }
    
    // If no concern category selected, return all subcategories
    if (!selectedConcernCategory) {
      if (typeof dummyAdvancedFilterData.concern_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.concern_subcategories)) {
        // Flatten all subcategories from all categories
        return Object.values(dummyAdvancedFilterData.concern_subcategories).flat();
      }
      return dummyAdvancedFilterData.concern_subcategories || [];
    }
    
    if (typeof dummyAdvancedFilterData.concern_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.concern_subcategories)) {
      const subcategories = dummyAdvancedFilterData.concern_subcategories[selectedConcernCategory] || [];
      return subcategories;
    }
    
    return dummyAdvancedFilterData.concern_subcategories || [];
  };

  // Enhanced handler for concern category changes
  const handleConcernCategoryChange = (e) => {
    const newConcernCategory = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      concernCategory: newConcernCategory
    }));
    
    if (newConcernCategory) {
      const validSubcategories = getConcernSubcategoriesForCategory(newConcernCategory);
      const currentSubcategory = advancedFilters.concernSubcategory;
      
      // If current subcategory is not valid for new category, clear it
      if (currentSubcategory && !validSubcategories.includes(currentSubcategory)) {
        setAdvancedFilters(prev => ({
          ...prev,
          concernSubcategory: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        concernSubcategory: ''
      }));
    }
  };

  // Function to get query category keys (for the query category dropdown)
  const getQueryCategoryKeys = () => {
    if (!dummyAdvancedFilterData.query_subcategories || typeof dummyAdvancedFilterData.query_subcategories !== 'object') {
      return dummyAdvancedFilterData.query_categories || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.query_subcategories);
    return keys;
  };

  const getQuerySubcategoriesForCategory = (selectedQueryCategory) => {
    if (!dummyAdvancedFilterData.query_subcategories) {
      return [];
    }
    
    // If no query category selected, return all subcategories
    if (!selectedQueryCategory) {
      if (typeof dummyAdvancedFilterData.query_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.query_subcategories)) {
        // Flatten all subcategories from all categories
        return Object.values(dummyAdvancedFilterData.query_subcategories).flat();
      }
      return dummyAdvancedFilterData.query_subcategories || [];
    }
    
    if (typeof dummyAdvancedFilterData.query_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.query_subcategories)) {
      const subcategories = dummyAdvancedFilterData.query_subcategories[selectedQueryCategory] || [];
      return subcategories;
    }
    
    return dummyAdvancedFilterData.query_subcategories || [];
  };
  // Custom Module helpers
  const getCustomModuleKeys = () => {
    if (!Array.isArray(dummyAdvancedFilterData.custom_module)) return [];
    return dummyAdvancedFilterData.custom_module.map(([key]) => key);
  };
  const getCustomModuleValues = (selectedParameter) => {
    if (!Array.isArray(dummyAdvancedFilterData.custom_module) || !selectedParameter) return [];
    const match = dummyAdvancedFilterData.custom_module.find(([key]) => key === selectedParameter);
    return match ? (Array.isArray(match[1]) ? match[1] : []) : [];
  };
  const handleCustomModuleParameterChange = (e) => {
    const param = e.target.value;
    setAdvancedFilters(prev => ({
      ...prev,
      custom_module_parameter_name: param,
      custom_module_parameter_answer: getCustomModuleValues(param).includes(prev.custom_module_parameter_answer) ? prev.custom_module_parameter_answer : ''
    }));
  };

  // Enhanced handler for query category changes
  const handleQueryCategoryChange = (e) => {
    const newQueryCategory = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      queryCategory: newQueryCategory
    }));
    
    if (newQueryCategory) {
      const validSubcategories = getQuerySubcategoriesForCategory(newQueryCategory);
      const currentSubcategory = advancedFilters.querySubcategory;
      
      // If current subcategory is not valid for new category, clear it
      if (currentSubcategory && !validSubcategories.includes(currentSubcategory)) {
        setAdvancedFilters(prev => ({
          ...prev,
          querySubcategory: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        querySubcategory: ''
      }));
    }
  };

  useEffect(()=>{
    if(client_id && service_id && email){
      fetchFilterDetailsOnceCustomers();
    }
  }, [client_id, service_id, email]);
    
      const handleNextPage = () => {
        if (page < totalPages) {
            const newPage = page + 1;
            setPage(newPage); 
            get_customer_page_details(client_id, service_id, newPage); // ✅ Ensure only one call
        }
      };
    
      const handlePreviousPage = () => {
          if (page > 1) {
              const newPage = page - 1;
              setPage(newPage);
              get_customer_page_details(client_id, service_id, newPage); // ✅ Ensure only one call
          }
      };
      const updateBusinessInsights = (index, field, value) => {
        setAdvancedFilters((prev) => ({
          ...prev,
          business_insights_multiple: prev.business_insights_multiple.map((item, i) => i === index ? { ...item, [field]: value } : item)
        }));
      };
      const isInitializingRef = useRef(false); // to prevent API calls during initialization
    
      useEffect(()=> {
        isInitializingRef.current = true;
        const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
          if (user) {
            setEmail(user.email);
          }
          if(serviceID){
            setService_id(serviceID)
          }
          if(clientID){
            setClient_id(clientID)
          }
          if(type){
            setOpenType(type)
          }
          if(segment){
            setSegmentParameterData(segment)
            if (segment.filter_applied?.time_series_bucket_filter) {
              setAdvancedFilters((prev) => ({
                ...prev,
                time_series_bucket_filter: segment.filter_applied.time_series_bucket_filter,
              }));
            }
            if(segment.filter_applied?.time || segment.filter_applied?.lastinteraction || segment.filter_applied?.last_interaction_time){
              const lastInteractionValue = segment.filter_applied?.last_interaction_time ?? segment.filter_applied?.lastinteraction ?? segment.filter_applied?.time;
              if(lastInteractionValue){
                handleQuickFilterChange({'target': {'value': lastInteractionValue}}, 'lastinteraction')
                if(lastInteractionValue === 'Custom Range'){
                  const lastInteractionRangeType =
                    String(segment.filter_applied?.lastinteraction_range_type || "").toLowerCase() ===
                    "after"
                      ? "after"
                      : "between";
                  setFilters((prev) => ({
                    ...prev,
                    ['Last Interaction Start']: segment.filter_applied['Last Interaction Start'] || '',
                    ['Last Interaction End']:
                      lastInteractionRangeType === "after"
                        ? ""
                        : (segment.filter_applied['Last Interaction End'] || ''),
                  }));
                  setFilters((prev) => ({
                    ...prev,
                    lastinteraction_range_type: lastInteractionRangeType,
                  }));
                }
              }
            }
            if(segment.filter_applied?.employee || segment.filter_applied?.employee_id){
              handleQuickFilterChange({'target': {'value': segment.filter_applied.employee || segment.filter_applied.employee_id}}, 'employee')
            }
            if(segment.filter_applied?.team_name || segment.filter_applied?.team){
              handleQuickFilterChange({'target': {'value': segment.filter_applied.team_name || segment.filter_applied.team}}, 'team_name')
            }
            if(segment.filter_applied?.firstinteraction || segment.filter_applied?.first_interaction_time){
              handleQuickFilterChange({'target': {'value': segment.filter_applied.firstinteraction || segment.filter_applied.first_interaction_time}}, 'firstinteraction')
              if(segment.id && segment.name){ 
                setFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
              if(segment.filter_applied?.firstinteraction === 'Custom Range' || segment.filter_applied?.first_interaction_time === 'Custom Range'){
                const firstInteractionRangeType =
                  String(segment.filter_applied?.firstinteraction_range_type || "").toLowerCase() ===
                  "after"
                    ? "after"
                    : "between";
                setFilters((prev) => ({
                  ...prev,
                  ['First Interaction Start']: segment.filter_applied['First Interaction Start'] || '',
                  ['First Interaction End']:
                    firstInteractionRangeType === "after"
                      ? ""
                      : (segment.filter_applied['First Interaction End'] || ''),
                }));
                setFilters((prev) => ({
                  ...prev,
                  firstinteraction_range_type: firstInteractionRangeType,
                }));
              }
            }
            if(segment.filter_applied?.customer_id){
              handleQuickFilterChange({'target': {'value': segment.filter_applied.customer_id}}, 'customer_id')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.customerPotential){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.customerPotential}}, 'customerPotential')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.interactions){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.interactions}}, 'interactions')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.openConcerns){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.openConcerns}}, 'openConcerns')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.customer_outcome){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.customer_outcome}}, 'customer_outcome')
              // setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.businessInsightParameter && segment.filter_applied?.businessInsightValue){
              setAdvancedFilters((prev) => ({ ...prev, business_insights_multiple: [{'business_insights_parameter': segment.filter_applied.businessInsightParameter, 'business_insights_parameter_value': segment.filter_applied.businessInsightValue}] }));
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.business_insights_multiple){
              setAdvancedFilters((prev) => ({ ...prev, business_insights_multiple: segment.filter_applied.business_insights_multiple }));
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if (
              segment.filter_applied?.custom_module_parameter_name &&
              segment.filter_applied?.custom_module_parameter_answer !== undefined &&
              segment.filter_applied?.custom_module_parameter_answer !== null &&
              segment.filter_applied?.custom_module_parameter_answer !== ''
            ) {
              setAdvancedFilters((prev) => ({
                ...prev,
                custom_module_parameter_name: segment.filter_applied.custom_module_parameter_name,
                custom_module_parameter_answer: segment.filter_applied.custom_module_parameter_answer,
              }));
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if (Array.isArray(segment.filter_applied?.custom_module_multiple)) {
              setAdvancedFilters((prev) => ({
                ...prev,
                custom_module_multiple: segment.filter_applied.custom_module_multiple,
              }));
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.concernCategory){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.concernCategory}}, 'concernCategory')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.concernSubcategory){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.concernSubcategory}}, 'concernSubcategory')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.queryCategory){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.queryCategory}}, 'queryCategory')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.querySubcategory){ 
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.querySubcategory}}, 'querySubcategory')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.disposition){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.disposition}}, 'disposition')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.subdisposition){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.subdisposition}}, 'subdisposition')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.lead_source){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.lead_source}}, 'lead_source')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.primary_language){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.primary_language}}, 'primary_language')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.secondary_languages){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.secondary_languages}}, 'secondary_languages')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.lead_sub_source){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.lead_sub_source}}, 'lead_sub_source')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.tag){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.tag}}, 'tag')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.crm_lead_stage){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.crm_lead_stage}}, 'crm_lead_stage')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.campaign_id){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.campaign_id}}, 'campaign_id')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.crm_lead_sub_stage){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.crm_lead_sub_stage}}, 'crm_lead_sub_stage')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.customer_phone_number){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.customer_phone_number}}, 'customer_phone_number')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            if(segment.filter_applied?.crm_location){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.crm_location}}, 'crm_location')
              if(segment.id && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.id]: segment.name ? segment.name : "Yes" }));
              }
            }
            applyCrmLeadCreatedAtFromSegmentFilter(segment.filter_applied, setAdvancedFilters);
          }
          if(role === 'employee'){
            handleQuickFilterChange({'target': {'value': user.name}}, 'employee')
          }
          const initTimeout = setTimeout(() => {
            isInitializingRef.current = false;
          }, 100);
          
          return () => {
            clearTimeout(initTimeout);
          };
      }, [serviceID, clientID, segment, role])

      // Handle page navigation
      useEffect(() => {
          if(client_id && service_id && email){
            get_customer_page_details(client_id, service_id, page);
          }
        }, [client_id, service_id, email, page]);
        const filtersDebounceRef = useRef(null);
        const firstFiltersLoadRef = useRef(true);
        useEffect(() => {
          if (!client_id || !service_id || !email) return;
      
          // Skip debounce logic on initial mount to avoid duplicate first fetch
          if (firstFiltersLoadRef.current) {
            firstFiltersLoadRef.current = false;
            return;
          }
          if (isInitializingRef.current) {
            return;
          }
      
          // Clear previous debounce timer
          if (filtersDebounceRef.current) {
            clearTimeout(filtersDebounceRef.current);
          }
      
          filtersDebounceRef.current = setTimeout(() => {
            // Only reset to first page; page effect will handle fetching once
            if (page !== 1) {
              setPage(1);
            } else {
                get_customer_page_details(client_id, service_id, 1);
            }
          }, 600); // idle delay
      
          return () => {
            if (filtersDebounceRef.current) {
              clearTimeout(filtersDebounceRef.current);
            }
          };
        }, [filters, advancedFilters, client_id, service_id, email]);

        const handleCustomerPage = (id) => {
          // Save current scroll position before opening details
          if (tableScrollRef.current) {
            savedScrollTopRef.current = tableScrollRef.current.scrollTop || 0;
          }
          if (openType === 'modal-open'){
            setSelectedCustomerID(id)
            setShowCustomerPage(true);
            return;
          }
          if (!segment && !type && client_id && service_id) {
            const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
            const queryParameterFilters = buildParameterQueryFilters(moduleParameterFilters);
            const advancedFiltersForUrl = { ...advancedFilters };
            delete advancedFiltersForUrl.business_insights_multiple;
            delete advancedFiltersForUrl.custom_module_multiple;
            delete advancedFiltersForUrl.custom_module_parameter_name;
            delete advancedFiltersForUrl.custom_module_parameter_answer;
            updateUrlWithFilters(navigate, location, { ...filters, ...queryParameterFilters }, advancedFiltersForUrl, '', '', page, { pageParam: 'custPage' });
          }
          // Properly encode the customer ID to handle special characters, spaces, and other URL-unsafe characters
          const encodedCustomerId = encodeURIComponent(id);
          navigate(`/analytics/${client_id}/${service_id}/customers/${encodedCustomerId}`)
        }
        const handleClose = (e) => {
          setShowCustomerPage(false);
          setSelectedCustomerID(null);
        }

        // After details page closes and list remounts, restore saved scroll position
        useEffect(() => {
          if (!showCustomerPage && tableScrollRef.current) {
            tableScrollRef.current.scrollTop = savedScrollTopRef.current || 0;
          }
        }, [showCustomerPage]);

        // Intercept Escape while viewing Customer Details so the outer modal is not closed
        useEffect(() => {
          if (!showCustomerPage) return;
          const onKeyDownCapture = (e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              // Try to stop other global handlers (like parent modal close)
              if (typeof e.stopImmediatePropagation === 'function') {
                e.stopImmediatePropagation();
              }
              e.stopPropagation();
              setShowCustomerPage(false);
            }
          };
          window.addEventListener('keydown', onKeyDownCapture, true);
          return () => window.removeEventListener('keydown', onKeyDownCapture, true);
        }, [showCustomerPage]);
        const getBusinessInsightValues = (selectedParameter) => {
          if (!selectedParameter || !dummyAdvancedFilterData.business_insight_parameters) return [];
          
          // If business_insights_parameters is an object with key-value pairs
          if (typeof dummyAdvancedFilterData.business_insight_parameters === 'object' && !Array.isArray(dummyAdvancedFilterData.business_insight_parameters)) {
            // Convert display name back to key format
            const keyFormat = selectedParameter.toLowerCase().replace(/ /g, '_');
            return dummyAdvancedFilterData.business_insight_parameters[keyFormat] || [];
          }
          
          // If business_insights_parameters is an array of arrays like [['key', ['values']], ...]
          if (Array.isArray(dummyAdvancedFilterData.business_insight_parameters)) {
            const selectedInsight = dummyAdvancedFilterData.business_insight_parameters.find(
              ([key]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) === selectedParameter
            );
            return selectedInsight ? selectedInsight[1] : [];
          }
          
          return [];
        };
      
        // Get business insight parameter keys (formatted for display)
        const getBusinessInsightKeys = () => {
          if (!dummyAdvancedFilterData.business_insight_parameters) return [];
          
          // If it's an object, get the keys
          if (typeof dummyAdvancedFilterData.business_insight_parameters === 'object' && !Array.isArray(dummyAdvancedFilterData.business_insight_parameters)) {
            return Object.keys(dummyAdvancedFilterData.business_insight_parameters).map(key => 
              key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            );
          }
          
          // If it's an array of arrays, get the first element of each sub-array
          if (Array.isArray(dummyAdvancedFilterData.business_insight_parameters)) {
            return dummyAdvancedFilterData.business_insight_parameters.map(([key]) => 
              key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            );
          }
          
          return [];
        };
        useEffect(() => {
          const handleEsc = (event) => {
            if (event.key === "Escape") {
              setShowAdvancedFilters(false);
            }
          }
          if (showAdvancedFilters) {
            window.addEventListener('keydown', handleEsc);
          }
          return () => {
            window.removeEventListener('keydown', handleEsc);
          }
        }, [showAdvancedFilters])
    
    return (
        <div className="flex flex-col font-sans bg-white" style={{height: "100%"}}>
          {!showCustomerPage && <main className={`flex-1 py-3 px-5 bg-gray-50 ${isEmbeddedModal ? 'flex min-h-0 flex-col overflow-hidden' : ''}`}>
            <div className="mb-4 shrink-0 pr-8">
                <h1 className="text-xl font-semibold text-gray-900 mb-2">Customer Records</h1>
                <p className="text-sm text-gray-500">View and manage all customer information and interactions</p>
            </div>

            {/* Active Filters */}
            {advancedFilters.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                <div className="flex flex-wrap gap-2">
                    {advancedFilters.map((filter, index) => (
                    <div key={index} className="flex items-center bg-gray-100 rounded-full px-3 py-1 text-sm">
                        <span className="mr-1">{filter.type}:</span>
                        <span className="font-medium">{filter.value}</span>
                        <button 
                        className="ml-2 text-gray-500 hover:text-gray-700"
                        >
                        <FaClose className="h-3 w-3" />
                        </button>
                    </div>
                    ))}
                    {advancedFilters.length > 0 && (
                    <button 
                        className="text-sm text-blue-600 hover:text-blue-800"
                        onClick={() => setAdvancedFilters([])}
                    >
                        Clear all
                    </button>
                    )}
                </div>
                </div>
            )}

            {exactCountDrilldown && appliedFilterSummary.length > 0 ? (
              <div className="mb-4 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3">
                <p className="text-xs text-indigo-700">
                  These report filters are always enforced. Filters below can only narrow these records.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {appliedFilterSummary.map((item, index) => (
                    <span
                      key={`${item?.label || "filter"}-${index}`}
                      className="rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs text-indigo-800"
                    >
                      <span className="font-medium">{item?.label || "Filter"}:</span>{" "}
                      {item?.value || "-"}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Primary Filter Section */}
            {!shouldHideFilters ? (
            <QuickFiltersPanel>
                <QuickFiltersHeader>
                <button
                  onClick={() => setIsDownloadModalOpen(true)}
                  className="flex items-center px-4 py-2 rounded text-sm text-white hover:opacity-90"
                  style={{ backgroundColor: "rgb(79, 70, 229)" }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </button>
                <button 
                    onClick={toggleAdvancedFilterPanel}
                    className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
                >
                    <FaFilter className="h-4 w-4 mr-1" />
                    Show Advanced Filters
                </button>
                </QuickFiltersHeader>
                <QuickFiltersRow layout="grid">
              
                    <input class="global-input"value={filters.customer_id} type="text" maxLength={500} placeholder="Customer ID" onChange={(e) => handleQuickFilterChange(e, 'customer_id')} 
                    className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input" />
                
                <ModernTimeDropdown placeholder="Team" value={filters.team_name} onChange={(e) => handleQuickFilterChange(e, 'team_name')} options={dummyAdvancedFilterData.teams} isMulti={isMultiSelectField('team_name')}/>
                <ModernTimeDropdown isDisabled={role === 'employee'} placeholder="Employee" value={filters.employee} onChange={(e) => handleQuickFilterChange(e, 'employee')} options={dummyAdvancedFilterData.employees} isMulti={isMultiSelectField('employee')}/>
                <ModernTimeDropdown placeholder="First Interaction Time" value={filters.firstinteraction} onChange={(e) => handleQuickFilterChange(e, 'firstinteraction')} options={dummyFilterData.firstinteraction} isMulti={isMultiSelectField('firstinteraction')}/>
                <ModernTimeDropdown placeholder="Last Interaction Time" value={filters.lastinteraction} onChange={(e) => handleQuickFilterChange(e, 'lastinteraction')} options={dummyFilterData.lastinteraction} isMulti={isMultiSelectField('lastinteraction')}/>
                </QuickFiltersRow>
                <div>
                {filters.lastinteraction === "Custom Range" && (
                      <div className="mb-2 mt-2">
                        <ModernDateRangePicker
                          startDate={filters['Last Interaction Start']}
                          endDate={filters['Last Interaction End']}
                          rangeType={filters.lastinteraction_range_type || "between"}
                          maxRangeDays={90}
                          placeholder="Select Last Interaction Range"
                          onChange={({ startDate, endDate, rangeType }) =>
                            setFilters((prev) => ({
                              ...prev,
                              'Last Interaction Start': startDate,
                              'Last Interaction End': endDate,
                              lastinteraction_range_type: rangeType || "between",
                            }))
                          }
                        />
                      </div>
                    )}
                    {filters.firstinteraction === "Custom Range" && (
                      <div className="mb-2 mt-2">
                        <ModernDateRangePicker
                          startDate={filters['First Interaction Start']}
                          endDate={filters['First Interaction End']}
                          rangeType={filters.firstinteraction_range_type || "between"}
                          maxRangeDays={90}
                          placeholder="Select First Interaction Range"
                          onChange={({ startDate, endDate, rangeType }) =>
                            setFilters((prev) => ({
                              ...prev,
                              'First Interaction Start': startDate,
                              'First Interaction End': endDate,
                              firstinteraction_range_type: rangeType || "between",
                            }))
                          }
                        />
                      </div>
                    )}
                </div>
                {/* Filter Display */}
                <AppliedFiltersRow>
                    {Object.keys(filters).map(
                      (key) => {
                        if (
                          key === 'First Interaction Start' ||
                          key === 'First Interaction End' ||
                          key === 'Last Interaction Start' ||
                          key === 'Last Interaction End' ||
                          key === 'firstinteraction_range_type' ||
                          key === 'lastinteraction_range_type'
                        ) {
                          return null;
                        }
                        if (key === 'firstinteraction' && filters.firstinteraction === 'Custom Range') {
                          return null;
                        }
                        if (key === 'lastinteraction' && filters.lastinteraction === 'Custom Range') {
                          return null;
                        }
                        return (Array.isArray(filters[key]) ? filters[key].length > 0 : filters[key]) && (
                          <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{key} - {Array.isArray(filters[key]) ? (filters[key].length > 2 ? `${filters[key].slice(0, 2).join(', ')} +${filters[key].length - 2} more` : filters[key].join(', ')) : filters[key]}</span>
                            {(key!=='lastinteraction' && key !== segmentParameterData.id && !(role === 'employee' && key === 'employee')) && <button
                              className="ml-2 text-gray-600"
                              onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
                            >
                              <FaTimes />
                            </button>}
                          </div>
                        )
                      })}
                      {filters.firstinteraction === 'Custom Range' && filters['First Interaction Start'] && (filters.firstinteraction_range_type === "after" || filters['First Interaction End']) && (
                        <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                          <span className="text-indigo-600">
                            First Interaction - {filters['First Interaction Start']} {filters.firstinteraction_range_type === "after" ? "onwards" : `to ${filters['First Interaction End']}`}
                          </span>
                          <button
                            className="ml-2 text-gray-600"
                            onClick={() =>
                              setFilters((prev) => ({
                                ...prev,
                                firstinteraction: '',
                                firstinteraction_range_type: 'between',
                                'First Interaction Start': '',
                                'First Interaction End': '',
                              }))
                            }
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                      {filters.lastinteraction === 'Custom Range' && filters['Last Interaction Start'] && (filters.lastinteraction_range_type === "after" || filters['Last Interaction End']) && (
                        <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                          <span className="text-indigo-600">
                            Last Interaction - {filters['Last Interaction Start']} {filters.lastinteraction_range_type === "after" ? "onwards" : `to ${filters['Last Interaction End']}`}
                          </span>
                          <button
                            className="ml-2 text-gray-600"
                            onClick={() =>
                              setFilters((prev) => ({
                                ...prev,
                                lastinteraction: '',
                                lastinteraction_range_type: 'between',
                                'Last Interaction Start': '',
                                'Last Interaction End': '',
                              }))
                            }
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                      {/* Show advanced filters that are set (excluding defaults) */}
                      {Object.entries(moduleParameterQueryFilters).map(([key, val]) => {
                        if (filters[key] !== undefined) return null;
                        if (key.endsWith('_lower') || key.endsWith('_upper')) return null;
                        return (
                          <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{key} - {String(val)}</span>
                            <button
                              className="ml-2 text-gray-600"
                              onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        );
                      })}
                      {Array.from(
                        new Set(
                          Object.keys(moduleParameterQueryFilters)
                            .filter((key) => key.endsWith('_lower') || key.endsWith('_upper'))
                            .map((key) => key.replace(/_(lower|upper)$/, ''))
                        )
                      ).map((baseKey) => {
                        const lower = moduleParameterQueryFilters[`${baseKey}_lower`];
                        const upper = moduleParameterQueryFilters[`${baseKey}_upper`];
                        if (lower === undefined && upper === undefined) return null;
                        return (
                          <div key={`${baseKey}-range`} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{baseKey} - {String(lower ?? '')} to {String(upper ?? '')}</span>
                            <button
                              className="ml-2 text-gray-600"
                              onClick={() =>
                                setFilters((prev) => ({
                                  ...prev,
                                  [`${baseKey}_lower`]: '',
                                  [`${baseKey}_upper`]: '',
                                }))
                              }
                            >
                              <FaTimes />
                            </button>
                          </div>
                        );
                      })}
                      {Object.entries(advancedFilters).map(([key, val]) => {
                        // skip if blank or default
                        if (
                          val === ''
                        ) {
                          return null;
                        }
                        if (key === 'business_insights_multiple' || key === 'custom_module_multiple' || key === 'custom_module_parameter_name' || key === 'custom_module_parameter_answer') {
                          return null;
                        }
                        if (shouldHideCrmLeadCreatedAtChipKey(key)) {
                          return null;
                        }
                        if(key !== 'business_insights_multiple'){
                        return (
                          <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{key} - {String(val)}</span>
                            <button
                              className="ml-2 text-gray-600"
                              onClick={() => setAdvancedFilters((prev) => clearAdvancedFilterField(prev, key))}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        );
                      }
                      })}
                      <CrmLeadCreatedAtFilterChips
                        advancedFilters={advancedFilters}
                        setAdvancedFilters={setAdvancedFilters}
                      />
                    </AppliedFiltersRow>
            </QuickFiltersPanel>
            ) : null}

            {/* Advanced Filter Panel (appears from the right) */}
            {!shouldHideFilters && showAdvancedFilters && (
              <CustomerAdvancedFiltersPanel
                isOpen={showAdvancedFilters}
                onClose={toggleAdvancedFilterPanel}
                multiSelectFields={multiSelectFields}
                advancedFilters={advancedFilters}
                setAdvancedFilters={setAdvancedFilters}
                handleAdvancedInputChange={handleAdvancedInputChange}
                dummyAdvancedFilterData={dummyAdvancedFilterData}
                expandedSections={expandedSections}
                setExpandedSections={setExpandedSections}
                updateBusinessInsights={updateBusinessInsights}
                getBusinessInsightKeys={getBusinessInsightKeys}
                getBusinessInsightValues={getBusinessInsightValues}
                handleConcernCategoryChange={handleConcernCategoryChange}
                getConcernCategoryKeys={getConcernCategoryKeys}
                getConcernSubcategoriesForCategory={getConcernSubcategoriesForCategory}
                handleQueryCategoryChange={handleQueryCategoryChange}
                getQueryCategoryKeys={getQueryCategoryKeys}
                getQuerySubcategoriesForCategory={getQuerySubcategoriesForCategory}
                handleCustomModuleParameterChange={handleCustomModuleParameterChange}
                getCustomModuleKeys={getCustomModuleKeys}
                getCustomModuleValues={getCustomModuleValues}
                handleDispositionChange={handleDispositionChange}
                getDispositionKeys={getDispositionKeys}
                getSubdispositionsForDisposition={getSubdispositionsForDisposition}
                crmStageKey="crm_lead_stage"
                crmSubStageKey="crm_lead_sub_stage"
                showDispositionSection={true}
                showCrmLeadCreatedAt={true}
              />
            )}
            <CustomerDownloadParametersModal 
              filterMode={filterMode}
              setFilterMode={setFilterMode}
              setCustomerIds={setCustomerIds}
              isOpen={isDownloadModalOpen}
              onClose={() => setIsDownloadModalOpen(false)}
              quickFilterApproxCount={totalRecords}
              onApply={captureRecords}
            />

            

            {/* Table */}
            <div
              ref={tableScrollRef}
              className={`bg-white rounded-lg shadow-sm ${isEmbeddedModal ? 'min-h-0 flex-1 overflow-auto' : ''}`}
              style={isEmbeddedModal ? undefined : {height: "52vh", overflowY: "auto"}}
            >
                {
                    loading ? (
                        <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                            <div className="loader"></div>
                            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead style={{ position: "sticky", top: "0" }}>
                                <tr>
                                <th scope="col" aria-sort={getAriaSort(sortConfig, 'id')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                                  <button type="button" className="flex items-center group" onClick={() => cycleSort('id', 'main')}>
                                    <span>Customer ID</span>
                                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('id')}</span>
                                  </button>
                                </th>
                                <th scope="col" aria-sort={getAriaSort(sortConfig, 'latestemployeeid')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                                  <button type="button" className="flex items-center group" onClick={() => cycleSort('latestemployeeid', 'main')}>
                                    <span>Employee Name</span>
                                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('latestemployeeid')}</span>
                                  </button>
                                </th>
                                <th scope="col" aria-sort={getAriaSort(sortConfig, 'firstinteraction')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                                  <button type="button" className="flex items-center group" onClick={() => cycleSort('firstinteraction', 'main')}>
                                    <span>First Interaction Time</span>
                                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('firstinteraction')}</span>
                                  </button>
                                </th>
                                <th scope="col" aria-sort={getAriaSort(sortConfig, 'lastinteraction')} className="px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider" style={{background: "white", fontSize: "13px", fontWeight: "400"}}>
                                  <button type="button" className="flex items-center group" onClick={() => cycleSort('lastinteraction', 'main')}>
                                    <span>Last interaction Time</span>
                                    <span className="ml-1 flex flex-col leading-none">{renderMainArrows('lastinteraction')}</span>
                                  </button>
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Action</th>
                                {/* <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Customer outcome</th> */}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {displayConversations.map((customer) => (
                                <tr key={customer.id} className="hover:bg-gray-50">
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-blue-600" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{customer.id}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{customer.latestemployeeid}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{formatCustomerRecordDate(customer.firstinteraction)}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{formatCustomerRecordDate(customer.lastinteraction)}</td>
                                    {/* <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>
                                    {customer.outcome}
                                    </td> */}
                                    <td onClick={()=>handleCustomerPage(customer.id)} className="px-5 py-3 whitespace-nowrap text-sm text-blue-600" style={{fontSize: "0.7rem", lineHeight: "1rem", cursor: "pointer"}}>View Details</td>
                                </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                }
            </div>
            {/* Pagination */}
            <div className="shrink-0 bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <div onClick={handlePreviousPage} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Previous
                </div>
                <div onClick={handleNextPage} className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Next
                </div>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(50*(page-1))+1}</span> to <span className="font-medium">{totalRecords <= 50 ? totalRecords : 50*page}</span> of <span className="font-medium">{totalRecords}</span> records
                  </p>
                </div>
                <div>
                  <nav className="z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <div onClick={handlePreviousPage} className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" style={{cursor: "pointer"}}>
                      Previous
                    </div>
                    <div onClick={handleNextPage} className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" style={{cursor: "pointer"}}>
                      Next
                    </div>
                  </nav>
                </div>
              </div>
            </div>
            </main>}
            {
          showCustomerPage && (
            <div className={isEmbeddedModal ? "min-h-0 flex-1 overflow-auto" : ""}>
              <CustomerDetails selectedOption={service_id} open_type={handleClose} customerIdProp={selectedCustomerID} selectedClientId={client_id}/>
            </div>
          )
        }
        <style jsx>
            {`
            .sections-headings {
                            cursor: pointer;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 15px;
                            border-radius: 10px;
                            transition: background 0.3s;
                            width: 100%;
                            }

                            .section-title {
                            font-size: 15px;
                            font-weight: 500;
                            display: flex;
                            align-items: center;
                            }
            `}              
          </style>
        </div>
    )
}

export default CustomerRecords
